/**
 * The TradeSync API client, shaped like the Base44 SDK.
 *
 * This is the keystone of the port. Every page and component in web/src calls
 * `base44.something`, so keeping that surface identical is what lets roughly
 * 7,500 lines of React carry over without being rewritten.
 *
 * Three jobs:
 *
 *   1. Talk to api.mytradesync.com instead of Base44.
 *   2. Translate field names. Base44 called a contact a MeetingRequest with a
 *      `trade_show_id`; the new database calls it a contact with a `show_id`.
 *      The React code still says `trade_show_id`, and it is translated here so
 *      it never has to change.
 *   3. Fail loudly and clearly for the features that were deliberately cut or
 *      deferred, rather than silently doing nothing.
 *
 * Anything named `notBuiltYet` or `wasCut` below is intentional. See the map at
 * the bottom of the file for what happens to each one and when.
 */

const TOKEN_KEY = 'tradesync_token'

/**
 * Empty in development, where vite proxies /api and /m to the local Worker.
 * In production this is https://api.mytradesync.com, set at build time.
 */
const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/+$/, '')

// ---------------------------------------------------------------------------
// token
// ---------------------------------------------------------------------------

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    // Safari in private mode throws on localStorage.
    return null
  }
}

function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* nothing we can do, the session just will not persist */
  }
}

// ---------------------------------------------------------------------------
// transport
// ---------------------------------------------------------------------------

/**
 * An error shaped like the one the Base44 SDK threw.
 *
 * AuthContext and several pages read `error.status`, so it has to be on the
 * error object rather than buried in a response.
 */
export class ApiError extends Error {
  constructor(status, body) {
    super(body?.message || body?.error || `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.error = body?.error ?? null
    this.data = body ?? null
  }
}

async function request(path, { method = 'GET', body, isForm = false, background = false } = {}) {
  const headers = {}
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  if (body && !isForm) headers['Content-Type'] = 'application/json'

  let response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: isForm ? body : body ? JSON.stringify(body) : undefined,
    })
  } catch (cause) {
    // Offline, or the API is unreachable. Status 0 so callers can tell this
    // apart from a real HTTP failure, which matters for the offline queue.
    const error = new ApiError(0, { message: 'Could not reach the server.' })
    error.cause = cause
    throw error
  }

  let payload = null
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    payload = await response.json().catch(() => null)
  }

  if (!response.ok) {
    // A dead or revoked session should not leave a stale token behind, for
    // an interactive call: the rep is looking at the screen right now, so
    // bouncing to /login is the right, visible outcome. `background` (the
    // offline outbox's own flush, task: stuck captures, 2026-09-08) is the
    // one exception: a 401 there happened on a timer or an 'online' event
    // with nobody watching, and clearing the token would silently sign the
    // rep out mid-show, the moment they next touch anything, over a request
    // they never knew ran. The outbox surfaces that case on its own terms
    // instead (offlineOutbox.js's authExpired), and the stored token is left
    // alone so a foreground action elsewhere is what actually decides
    // whether this rep gets bounced to /login, matching how AuthContext
    // already treats a network failure (status 0) as not proof of anything.
    if (response.status === 401 && !background) setToken(null)
    throw new ApiError(response.status, payload)
  }

  return payload
}

// ---------------------------------------------------------------------------
// field translation
// ---------------------------------------------------------------------------

/**
 * A contact from the API, wearing the field names the React code expects.
 *
 * Base44 gave every record `created_date`, `updated_date` and `created_by_id`.
 * The new schema uses `created_at`, `updated_at` and `scanned_by_user_id`. Both
 * sets are present on the returned object: the new names because they are the
 * truth, the old ones because the pages sort and filter on them.
 */
function toMeetingRequest(contact) {
  if (!contact) return contact
  return {
    ...contact,

    // Base44 names the pages still use.
    trade_show_id: contact.show_id,
    trade_show: contact.show_name,
    scanned_by: contact.scanned_by_user_id,
    created_date: contact.created_at,
    updated_date: contact.updated_at,
    created_by_id: contact.scanned_by_user_id,

    // Images are R2 keys now, not public URLs. These are paths on this API,
    // which needs the bearer token, so a component cannot drop one straight
    // into an <img src>. See fetchImageObjectUrl below.
    badge_image_url: contact.badge_image_key ? `/api/images/${contact.badge_image_key}` : null,
    badge_back_image_url: contact.badge_back_image_key ? `/api/images/${contact.badge_back_image_key}` : null,
    person_photo_url: contact.person_photo_key ? `/api/images/${contact.person_photo_key}` : null,
  }
}

/** The reverse, for create and update. Unknown keys are passed through. */
function fromMeetingRequest(data) {
  if (!data) return data
  const out = { ...data }

  if ('trade_show_id' in out) {
    out.show_id = out.trade_show_id
    delete out.trade_show_id
  }
  // Base44 stored the show name on the contact as well. The name lives on the
  // show now, so this is dropped rather than written.
  delete out.trade_show

  if ('scanned_by' in out) {
    out.scanned_by_user_id = out.scanned_by
    delete out.scanned_by
  }

  // Server owned. Sending them would be ignored at best.
  delete out.created_date
  delete out.updated_date
  delete out.created_by_id
  delete out.id

  return out
}

function toTradeShow(show) {
  if (!show) return show
  return {
    ...show,
    created_date: show.created_at,
    updated_date: show.updated_at,
    // Base44 held members as an array on the show. Silos are off (CLAUDE.md
    // section 2) and the member UI goes in task 2.2, but the field is present
    // so the pages that read it do not blow up first.
    member_ids: [],
  }
}

function fromTradeShow(data) {
  if (!data) return data
  const out = { ...data }
  delete out.created_date
  delete out.updated_date
  delete out.member_ids
  delete out.id
  return out
}

function toUser(user) {
  if (!user) return user
  return {
    ...user,
    created_date: user.created_at,
    updated_date: user.updated_at,
    // Base44's User had this; some screens read it.
    disabled: user.status !== 'active',
  }
}

// ---------------------------------------------------------------------------
// helpers for the entity surface
// ---------------------------------------------------------------------------

/** Base44 sorts looked like '-created_date'. Only the field and direction. */
function parseSort(sort) {
  if (typeof sort !== 'string' || sort.length === 0) return null
  return sort.startsWith('-') ? { field: sort.slice(1), desc: true } : { field: sort, desc: false }
}

function queryString(params) {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const text = search.toString()
  return text ? `?${text}` : ''
}

function notBuiltYet(what, when) {
  return () => {
    throw new ApiError(501, {
      error: 'not_built_yet',
      message: `${what} is not built yet. It arrives in ${when}.`,
    })
  }
}

function wasCut(what, why) {
  return () => {
    throw new ApiError(410, { error: 'removed', message: `${what} was removed. ${why}` })
  }
}

// ---------------------------------------------------------------------------
// auth
// ---------------------------------------------------------------------------

const auth = {
  async me() {
    const payload = await request('/api/auth/me')
    return toUser(payload.user)
  },

  async loginViaEmailPassword(email, password) {
    const payload = await request('/api/auth/login', {
      method: 'POST',
      body: { email, password },
    })
    setToken(payload.token)
    return toUser(payload.user)
  },

  isAuthenticated() {
    return Boolean(getToken())
  },

  /**
   * Base44 redirected to its own hosted login. There is no hosted login now,
   * so this ends the session and sends the browser to our own /login.
   */
  async logout(redirectTo) {
    try {
      await request('/api/auth/logout', { method: 'POST' })
    } catch {
      // Already expired or offline. The local token still goes.
    }
    setToken(null)
    if (typeof window !== 'undefined') {
      window.location.href = redirectTo && typeof redirectTo === 'string' ? '/login' : '/login'
    }
  },

  redirectToLogin() {
    if (typeof window !== 'undefined') window.location.href = '/login'
  },

  async resetPasswordRequest(email) {
    return request('/api/auth/forgot-password', { method: 'POST', body: { email } })
  },

  /**
   * A genuine reset (the account already had a password) just confirms
   * ok: true, same as before, the caller sends them to /login. Setting a
   * password for the first time via an invite link comes back with a
   * real session instead, since there was never anything to revoke and
   * the point of the link was to get them straight into the app.
   */
  async resetPassword(token, newPassword) {
    const payload = await request('/api/auth/reset-password', {
      method: 'POST',
      body: { token, new_password: newPassword },
    })
    if (payload.token) {
      setToken(payload.token)
      return { activated: true, user: toUser(payload.user) }
    }
    return { activated: false }
  },

  async changePassword(currentPassword, newPassword) {
    return request('/api/auth/change-password', {
      method: 'POST',
      body: { current_password: currentPassword, new_password: newPassword },
    })
  },

}

// ---------------------------------------------------------------------------
// entities
// ---------------------------------------------------------------------------

const MeetingRequest = {
  async list(sort, limit = 200) {
    // Deleted contacts are included because Home and other pages do their own
    // `status !== 'deleted'` filtering, exactly as they did against Base44.
    const payload = await request(
      `/api/contacts${queryString({ limit, include_deleted: true })}`,
    )
    const rows = payload.contacts.map(toMeetingRequest)
    const order = parseSort(sort)
    if (order) {
      rows.sort((a, b) => {
        const left = a[order.field] ?? ''
        const right = b[order.field] ?? ''
        return order.desc ? String(right).localeCompare(String(left)) : String(left).localeCompare(String(right))
      })
    }
    return rows
  },

  async filter(query = {}, sort, limit = 200) {
    const params = { limit }

    if (query.status === 'deleted') params.deleted = true
    else if (query.status) params.status = query.status
    else params.include_deleted = true

    if (query.trade_show_id) params.show_id = query.trade_show_id
    if (query.created_by_id) params.scanned_by = query.created_by_id
    if (query.scanned_by) params.scanned_by = query.scanned_by

    // The confirmed meeting flow is deferred (CLAUDE.md section 6) and the API
    // deliberately does not hand out accept_token, so this can only be empty.
    if (query.accept_token) {
      console.warn('accept_token lookup is part of the deferred v2 meeting flow.')
      return []
    }

    const payload = await request(`/api/contacts${queryString(params)}`)
    let rows = payload.contacts.map(toMeetingRequest)

    // Anything the API cannot filter on is filtered here, so the call still
    // means what the caller intended.
    const handled = new Set(['status', 'trade_show_id', 'created_by_id', 'scanned_by', 'accept_token'])
    const leftovers = Object.entries(query).filter(([key]) => !handled.has(key))
    if (leftovers.length > 0) {
      rows = rows.filter((row) => leftovers.every(([key, value]) => row[key] === value))
    }

    const order = parseSort(sort)
    if (order) {
      rows.sort((a, b) => {
        const left = a[order.field] ?? ''
        const right = b[order.field] ?? ''
        return order.desc ? String(right).localeCompare(String(left)) : String(left).localeCompare(String(right))
      })
    }
    return rows
  },

  async get(id) {
    const payload = await request(`/api/contacts/${id}`)
    return toMeetingRequest(payload.contact)
  },

  async create(data) {
    const payload = await request('/api/contacts', {
      method: 'POST',
      body: fromMeetingRequest(data),
    })
    return toMeetingRequest(payload.contact)
  },

  async update(id, data) {
    // Base44 deleted a contact by setting status to 'deleted'. The API keeps
    // status and deleted_at in step and refuses that, so it is routed to the
    // real delete instead. Restoring is the same story in reverse.
    const next = fromMeetingRequest(data)

    if (next.status === 'deleted') {
      delete next.status
      const payload = await request(`/api/contacts/${id}`, { method: 'DELETE' })
      if (Object.keys(next).length === 0) return toMeetingRequest(payload.contact)
      const updated = await request(`/api/contacts/${id}`, { method: 'PATCH', body: next })
      return toMeetingRequest(updated.contact)
    }

    if (next.status === 'saved') {
      const current = await request(`/api/contacts/${id}`).catch(() => null)
      if (current?.contact?.status === 'deleted') {
        delete next.status
        const restored = await request(`/api/contacts/${id}/restore`, {
          method: 'POST',
          body: next.show_id ? { show_id: next.show_id } : {},
        })
        delete next.show_id
        if (Object.keys(next).length === 0) return toMeetingRequest(restored.contact)
      }
    }

    const payload = await request(`/api/contacts/${id}`, { method: 'PATCH', body: next })
    return toMeetingRequest(payload.contact)
  },

  /**
   * Destroy a contact, photos and all.
   *
   * This is Base44's meaning of delete, and getting it wrong is easy: there,
   * a soft delete was `update(id, { status: 'deleted' })` and `delete()` was
   * the real thing. The Deleted Contacts screen calls this for its "Delete
   * Forever" button, and the trash icon on a contact card calls update.
   *
   * The API refuses unless the contact is already soft deleted.
   */
  async delete(id) {
    await request(`/api/contacts/${id}/permanent`, { method: 'DELETE' })
    return { ok: true, id }
  },

  /**
   * Manual, single-tap "Look up on ZoomInfo" / "Retry ZoomInfo lookup"
   * button on the Edit Contact page. Runs synchronously on the server (not
   * fire-and-forget like the automatic capture-time enrichment), so this
   * resolves with a real answer to show the rep, not just an updated
   * contact.
   */
  async zoominfoLookup(id) {
    const payload = await request(`/api/contacts/${id}/zoominfo-lookup`, { method: 'POST' })
    return { contact: toMeetingRequest(payload.contact), zoominfo: payload.zoominfo }
  },

  /**
   * Manual "Look up LinkedIn" button on the Edit Contact page. Web search
   * only, never ZoomInfo (narrowed 2026-09-25 so this never spends a
   * ZoomInfo credit): only ever produces an unverified suggestion, same as
   * the automatic capture-time path's own web search step. Only ever called
   * when the contact has no linkedin_url yet.
   */
  async linkedinLookup(id) {
    const payload = await request(`/api/contacts/${id}/linkedin-lookup`, { method: 'POST' })
    return { contact: toMeetingRequest(payload.contact), linkedin: payload.linkedin }
  },
}

const TradeShow = {
  async list(sort, limit = 50) {
    const payload = await request(`/api/shows${queryString({ include_inactive: true })}`)
    const rows = payload.shows.map(toTradeShow)
    const order = parseSort(sort)
    if (order) {
      rows.sort((a, b) => {
        const left = a[order.field] ?? ''
        const right = b[order.field] ?? ''
        return order.desc ? String(right).localeCompare(String(left)) : String(left).localeCompare(String(right))
      })
    }
    return rows.slice(0, limit)
  },

  async filter(query = {}, sort, limit = 50) {
    const rows = await TradeShow.list(sort, 500)
    const filtered = rows.filter((row) => Object.entries(query).every(([k, v]) => row[k] === v))
    return filtered.slice(0, limit)
  },

  async get(id) {
    const payload = await request(`/api/shows/${id}`)
    return toTradeShow(payload.show)
  },

  async create(data) {
    const payload = await request('/api/shows', { method: 'POST', body: fromTradeShow(data) })
    return toTradeShow(payload.show)
  },

  async update(id, data) {
    const payload = await request(`/api/shows/${id}`, { method: 'PATCH', body: fromTradeShow(data) })
    return toTradeShow(payload.show)
  },

  async delete(id) {
    const payload = await request(`/api/shows/${id}`, { method: 'DELETE' })
    return toTradeShow(payload.show)
  },

  /**
   * Which reps a device account's picker offers while capturing under this
   * show (task: per-show rep assignment, 2026-09-15). Admin only on the
   * server; an empty array means "no assignment", which the picker itself
   * (GET /api/reps?show_id=) treats as "offer everyone", not "offer nobody".
   */
  async getMembers(id) {
    const payload = await request(`/api/shows/${id}/members`)
    return payload.user_ids
  },

  async setMembers(id, userIds) {
    const payload = await request(`/api/shows/${id}/members`, { method: 'PUT', body: { user_ids: userIds } })
    return payload.user_ids
  },
}

const User = {
  async list() {
    const payload = await request('/api/users')
    return payload.users.map(toUser)
  },

  async get(id) {
    const payload = await request(`/api/users/${id}`)
    return toUser(payload.user)
  },

  /**
   * Creates the account as 'invited' with no password, and mails a link
   * to set one, from the admin's own Gmail. There is no password to show
   * here any more; `warning` is set instead if the account was created
   * but the invite email itself failed to send.
   */
  async create(data) {
    const payload = await request('/api/users', { method: 'POST', body: data })
    return { ...toUser(payload.user), warning: payload.warning ?? null }
  },

  /** Mails a fresh invite link. Only valid while the account is still 'invited'. */
  async resendInvite(id) {
    return request(`/api/users/${id}/resend-invite`, { method: 'POST' })
  },

  async update(id, data) {
    const payload = await request(`/api/users/${id}`, { method: 'PATCH', body: data })
    return toUser(payload.user)
  },

  /** A rep editing their own record. Always the caller, from the auth token, never a param. */
  async updateMyProfile(data) {
    const payload = await request('/api/profile', { method: 'PATCH', body: data })
    return toUser(payload.user)
  },

  /**
   * Issue a new temporary password and end every session the user had open.
   *
   * Not part of the Base44 surface: Base44 had no password of its own to
   * reset. Returns the new password, which is shown once and never again.
   */
  async resetPassword(id) {
    const payload = await request(`/api/users/${id}/reset-password`, { method: 'POST' })
    return {
      user: toUser(payload.user),
      temporary_password: payload.temporary_password,
      warning: payload.warning ?? null,
    }
  },
}

/**
 * Base44's AppSettings, backed by the key/value settings table.
 *
 * The record's `id` is its key. That is what lets the Templates screen keep
 * its original read, modify, write shape: it filters by key, then updates by
 * the id it got back.
 *
 * A missing setting is an empty list rather than an error, because "no
 * templates saved yet" is the normal state, not a failure.
 */
const AppSettings = {
  async filter(query = {}) {
    const key = query.key
    if (!key) return []
    try {
      const payload = await request(`/api/settings/${encodeURIComponent(key)}`)
      const setting = payload.setting
      return [
        {
          id: setting.key,
          key: setting.key,
          value: setting.value,
          created_date: setting.updated_at,
          updated_date: setting.updated_at,
        },
      ]
    } catch (error) {
      if (error.status === 404) return []
      throw error
    }
  },

  async list() {
    const payload = await request('/api/settings')
    return payload.settings.map((setting) => ({
      id: setting.key,
      key: setting.key,
      value: setting.value,
      created_date: setting.updated_at,
      updated_date: setting.updated_at,
    }))
  },

  async get(key) {
    const [found] = await AppSettings.filter({ key })
    if (!found) throw new ApiError(404, { error: 'not_found', message: 'No such setting.' })
    return found
  },

  async create({ key, value }) {
    const payload = await request(`/api/settings/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: { value },
    })
    return { id: payload.setting.key, ...payload.setting, updated_date: payload.setting.updated_at }
  },

  // The id is the key, which is why an update needs nothing else.
  async update(id, { value }) {
    const payload = await request(`/api/settings/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: { value },
    })
    return { id: payload.setting.key, ...payload.setting, updated_date: payload.setting.updated_at }
  },

  async delete(id) {
    await request(`/api/settings/${encodeURIComponent(id)}`, { method: 'DELETE' })
    return { ok: true }
  },
}

// Tables that no longer exist. See CLAUDE.md section 2 and db/schema.sql.
const cutEntity = (name, why) => ({
  list: wasCut(name, why),
  filter: wasCut(name, why),
  get: wasCut(name, why),
  create: wasCut(name, why),
  update: wasCut(name, why),
  delete: wasCut(name, why),
})

const entities = {
  MeetingRequest,
  TradeShow,
  User,
  AppSettings,
  RepAccount: cutEntity('RepAccount', 'It stored plaintext PINs. Reps are rows in users now.'),
  UserApproval: cutEntity('UserApproval', 'It had no rows. Account state is users.status now.'),
  Invitation: cutEntity('Invitation', 'It had no rows. An admin creates accounts directly.'),
}

// ---------------------------------------------------------------------------
// functions
// ---------------------------------------------------------------------------

const functionHandlers = {
  async createMeeting({ data, background = false }) {
    const payload = await request('/api/contacts', {
      method: 'POST',
      body: fromMeetingRequest(data),
      background,
    })
    return toMeetingRequest(payload.contact)
  },

  async manageUser({ action, userId, ...rest }) {
    if (action === 'suspend' || action === 'reactivate') {
      // rest carries reassign_contacts_to for a suspend of a rep who still
      // owns contacts (task: contact reassignment, 2026-09-08). Omitted
      // entirely for a rep who owns none; the API only requires it when it
      // matters.
      const payload = await request(`/api/users/${userId}/${action}`, {
        method: 'POST',
        body: action === 'suspend' && Object.keys(rest).length > 0 ? rest : undefined,
      })
      return { ok: true, user: toUser(payload.user), reassigned_contact_count: payload.reassigned_contact_count }
    }
    if (action === 'promote' || action === 'demote') {
      const payload = await request(`/api/users/${userId}`, {
        method: 'PATCH',
        body: { role: action === 'promote' ? 'admin' : 'user' },
      })
      return { ok: true, user: toUser(payload.user) }
    }
    if (action === 'remove') {
      throw new ApiError(410, {
        error: 'removed',
        message:
          'Deleting a user was removed. Contacts record who captured them, so suspend the account instead.',
      })
    }
    throw new ApiError(400, { error: 'invalid_action', message: `Unknown action: ${action}`, ...rest })
  },

  // Task 3.3 is built, but as base44.outbound.sendEmail/sendSms, not as a
  // reimplementation of this function: those send against an already saved
  // contact_id rather than a whole contact payload. Every caller of this was
  // moved to that instead. Left as a stub, not deleted, so a caller this
  // audit missed fails loudly with a message that says where to look, rather
  // than quietly resolving to nothing the way the real gap did.
  saveContact: wasCut('saveContact', 'Use base44.outbound.sendEmail(contactId) / sendSms(contactId) instead.'),
  // The per show doc is a link the owner pastes on the show, not a file this
  // app makes. Decided 2026-08-29: the assistant already writes and shares a
  // show information doc before anyone travels. See db/migrations/0004.
  appendContactToDoc: wasCut(
    'Appending contacts to a Google Doc',
    'the show document is written by hand. Contacts live in the app and the Sheet.',
  ),
  createEventDoc: wasCut(
    'Creating a Google Doc per show',
    'paste the link into Show document URL on the show instead.',
  ),

  // Deferred to v2, CLAUDE.md section 6. Used zero times in production.
  getAvailability: notBuiltYet('Picking a meeting slot', 'v2'),
  createZoomMeeting: notBuiltYet('Creating a Zoom meeting', 'v2'),
  addToCalendar: notBuiltYet('Adding a meeting to a calendar', 'v2'),
  sendMeetingInvite: notBuiltYet('Sending a confirmed meeting invite', 'v2'),

  // Cut outright.
  createCheckout: wasCut('Checkout', 'TradeSync is internal and is not sold.'),
  claimInvitation: wasCut('Invitations', 'An admin creates accounts directly.'),
  validateInvitation: wasCut('Invitations', 'An admin creates accounts directly.'),
  authenticateRep: wasCut('PIN sign in', 'It stored plaintext PINs. Use email and password.'),
}

const functions = {
  async invoke(name, payload = {}) {
    const handler = functionHandlers[name]
    if (!handler) {
      throw new ApiError(404, {
        error: 'unknown_function',
        message: `There is no function called ${name}.`,
      })
    }
    return handler(payload)
  },
}

// ---------------------------------------------------------------------------
// integrations
// ---------------------------------------------------------------------------

/**
 * Read only company facts, migration 0014. Not a Base44 entity: companies
 * did not exist as their own record there.
 */
const companiesApi = {
  get: (id) => request(`/api/companies/${id}`).then((payload) => payload.company),
}

/**
 * The reduced, non-admin-safe rep list, task: shared capture device
 * (2026-09-08). base44.entities.User.list() is admin only and a device
 * account never is one; this is what its rep picker calls instead.
 *
 * showId is optional (task: per-show rep assignment, 2026-09-15): narrows
 * the list to whichever reps are assigned to that show, falling back
 * server side to every rep when none are assigned.
 */
const repsApi = {
  list: (showId) =>
    request(`/api/reps${showId ? `?show_id=${encodeURIComponent(showId)}` : ''}`).then((payload) => payload.reps),
}

/** The shared contacts Sheet, task 3.5. */
const sheetsApi = {
  status: () => request('/api/sheets'),
  backfill: (all = false) => request(`/api/sheets/backfill${all ? '?all=true' : ''}`, { method: 'POST' }),
  share: () => request('/api/sheets/share', { method: 'POST' }),
}

/**
 * Admin only. Task: "Re-run ZoomInfo" phone backfill tool (2026-09-24):
 * every real contact already FULL_MATCHed under the old, wrong 'phone'
 * field name, still missing zoominfo_phone, gets one fresh, real ZoomInfo
 * lookup. phoneBackfillCandidates is read only, zero cost, for the
 * confirmation dialog; runPhoneBackfill is the real spend.
 */
const zoominfoApi = {
  phoneBackfillCandidates: () => request('/api/zoominfo/phone-backfill/candidates'),
  runPhoneBackfill: () => request('/api/zoominfo/phone-backfill', { method: 'POST' }),
  /**
   * Task: confirm 'mobilePhone' before it ever reaches a live contact,
   * 2026-09-24. One isolated call against ZoomInfo's own CEO (the same
   * fixed test subject test-auth already uses), never a real prospect,
   * and never anything that touches the shared enrichment path. Real
   * credit spend either way, same as test-auth.
   */
  testMobilePhoneField: () => request('/api/zoominfo/test-mobile-phone-field', { method: 'POST' }),
}

/**
 * The post show reminder sequence, task 3.6, and the four-email admin panel
 * built on top of it. Admin only, all of it.
 */
const remindersApi = {
  preview: () => request('/api/reminders/preview'),
  run: (dryRun = true) => request('/api/reminders/run', { method: 'POST', body: { dry_run: dryRun } }),
  testSend: (contactId, to) =>
    request('/api/reminders/test-send', { method: 'POST', body: { contact_id: contactId, to } }),

  /** The built in wording for all four emails. What "Reset to industry default" restores. */
  defaults: () => request('/api/reminders/defaults'),

  /**
   * Renders one of the four emails against a fabricated example contact, no
   * send. repId is optional (task: real rep data in the Templates preview,
   * 2026-09-24): omitted, the API renders against its own fabricated example
   * rep exactly as before (JSON.stringify drops an undefined value, so this
   * is never sent as a literal null); given, it renders against that rep's
   * own real name, phone, email and signature instead.
   *
   * show, `{ name, location }`, is also optional (task: real show data in
   * the Per-Event Override preview, same day): only Templates.jsx's
   * Per-Event Override panel has a real show in scope to pass, so every
   * other caller keeps seeing SAMPLE_SHOW's own fake name and location.
   */
  previewEmail: (kind, subject, body, repId, show) =>
    request('/api/reminders/preview-email', {
      method: 'POST',
      body: { kind, subject, body, rep_id: repId, show_name: show?.name, show_location: show?.location },
    }),

  /** "Send test to me": the same render, sent as the caller, prefixed [TEST]. */
  testSendEmail: (kind, subject, body, to) =>
    request('/api/reminders/test-send-email', { method: 'POST', body: { kind, subject, body, to } }),

  /** Renders the text message against the same fabricated example, no send. repId and show are optional, same as previewEmail's own. */
  previewSms: (template, repId, show) =>
    request('/api/reminders/preview-sms', {
      method: 'POST',
      body: { template, rep_id: repId, show_name: show?.name, show_location: show?.location },
    }),
}

/**
 * The follow up text and email, task 3.3. Built as its own route, not a
 * reimplementation of Base44's saveContact/sendMeetingInvite functions
 * (those stay stubs below, in functionHandlers), so anything that wants to
 * send now must call this directly rather than through base44.functions.invoke.
 */
const outboundApi = {
  previewEmail: (contactId) => request('/api/outbound/email/preview', { method: 'POST', body: { contact_id: contactId } }),
  sendEmail: (contactId) => request('/api/outbound/email', { method: 'POST', body: { contact_id: contactId } }),
  previewSms: (contactId) => request('/api/outbound/sms/preview', { method: 'POST', body: { contact_id: contactId } }),
  sendSms: (contactId) => request('/api/outbound/sms', { method: 'POST', body: { contact_id: contactId } }),
  /**
   * Native SMS handoff, task: send the Initial SMS from the rep's own
   * phone (2026-09-08). Called after the frontend has already opened the
   * phone's own Messages app; this only stamps sms_sent_at and logs the
   * attempt, never sends anything itself. `body`, the exact rendered text
   * just used to build the sms: URI (task: real send content on
   * outbound_log, 2026-09-24), lets that attempt show real content on the
   * contact detail page too, not just a timestamp; omit it and the log row
   * simply has no saved copy, same as before this existed.
   */
  smsNativeHandoff: (contactId, body) =>
    request('/api/outbound/sms/native-handoff', { method: 'POST', body: { contact_id: contactId, body } }),
  /**
   * The 5-touch send history for the contact detail page (task: real sent
   * content, not a generic template preview, 2026-09-24). See
   * worker/lib/contactTouches.ts.
   */
  getTouches: (contactId) => request(`/api/contacts/${contactId}/touches`),
  /**
   * The Next Action Card, task: Home screen redesign (2026-09-08). confirmed
   * must be passed as true: the API refuses otherwise, on purpose, so this
   * can never fire from anywhere that forgot to ask first. Returns
   * { sent, failed, results }, one entry per contact, so a partial failure
   * can be reported rather than hidden behind a single pass/fail.
   */
  sendBulk: (contactIds) => request('/api/outbound/bulk', { method: 'POST', body: { contact_ids: contactIds, confirmed: true } }),
}

const integrations = {
  Core: {
    /** Uploads a photo and returns something shaped like the Base44 result. */
    async UploadFile({ file, kind = 'person', background = false }) {
      const form = new FormData()
      form.append('image', file)
      const payload = await request(`/api/images${queryString({ kind })}`, {
        method: 'POST',
        body: form,
        isForm: true,
        background,
      })
      return { file_url: `/api/images/${payload.key}`, key: payload.key }
    },

    // Badge reading is one call now: POST /api/badge/scan takes the photo,
    // stores it and returns the fields. BadgeScanner.jsx is rewired to use
    // scanBadge below in task 2.3.
    InvokeLLM: notBuiltYet('The old two step upload then read flow', 'task 2.3, which uses scanBadge instead'),
  },
}

/**
 * Per rep Google connection.
 *
 * Not a Base44 surface at all. Base44 held one shared connection and sent
 * everything as Mike; each rep connects their own account now.
 */
export const google = {
  status: () => request('/api/oauth/google/status'),

  /** Returns the consent URL. The caller sends the browser there. */
  start: () => request('/api/oauth/google/start'),

  disconnect: () => request('/api/oauth/google/disconnect', { method: 'POST' }),
}

/**
 * Not part of the Base44 surface. The replacement for BadgeScanner's two
 * calls. backFile is optional (task: two-sided business card capture,
 * 2026-09-08): many cards put the contact details on the back, and both
 * images are read together in one OCR pass, not two merged separately.
 */
export async function scanBadge(file, backFile = null) {
  const form = new FormData()
  form.append('image', file)
  if (backFile) form.append('image_back', backFile)
  return request('/api/badge/scan', { method: 'POST', body: form, isForm: true })
}

/**
 * Uploads a rep's headshot. Its own function rather than routed through
 * integrations.Core.UploadFile above: that helper narrows the response down
 * to a single { file_url, key }, but a headshot upload returns two keys (a
 * 512px primary and a 128px thumbnail, worker/lib/images.ts's
 * processHeadshotUpload), and the caller needs both.
 *
 * honorExifOrientation should be false only when the file being sent is one
 * RepProfileFields.jsx already converted from HEIC to JPEG itself: see that
 * component's own comment on why a HEIC-origin JPEG's EXIF orientation
 * cannot be trusted the same way a native JPEG's can.
 */
export async function uploadHeadshot(file, { honorExifOrientation = true } = {}) {
  const form = new FormData()
  form.append('image', file)
  form.append('honor_exif_orientation', honorExifOrientation ? 'true' : 'false')
  return request('/api/images?kind=headshot', { method: 'POST', body: form, isForm: true })
}

/**
 * Fetch an image and return an object URL.
 *
 * Image routes need the bearer token, so `<img src="/api/images/...">` gets a
 * 401. Components call this and put the result in src instead. Revoke it with
 * URL.revokeObjectURL when the component unmounts.
 */
export async function fetchImageObjectUrl(path) {
  if (!path) return null
  const token = getToken()
  const response = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!response.ok) throw new ApiError(response.status, null)
  return URL.createObjectURL(await response.blob())
}

// ---------------------------------------------------------------------------

export const base44 = {
  auth,
  entities,
  functions,
  integrations,
  google,
  sheets: sheetsApi,
  reminders: remindersApi,
  outbound: outboundApi,
  companies: companiesApi,
  reps: repsApi,
  zoominfo: zoominfoApi,

  /**
   * Base44 let the browser act as the service role, which meant the frontend
   * could bypass its own row level security. That is not something to rebuild.
   */
  asServiceRole: {
    entities,
    connectors: {
      getConnection: wasCut(
        'Browser side service role access',
        'Google connections are per user and live on the server.',
      ),
    },
  },
}

export default base44
